<template>
    <base-card
        :key="event.id"
        :image="eventImage"
        :clickable="true"
        :cta="{
            text: cta.text,
            disabled: false
        }"
        :styles="{
            card: {
                maxWidth: '336px',
                height: '392px'
            },
            cta: {
                display: cta.display,
                background: variant === 'artist' ? '#cdeb09' : '#ba87ff'
            },
            content: {
                gap: '0'
            }
        }"
        @card-clicked="$emit('cta-clicked', event, cta.redirect)"
        @cta-clicked="$emit('cta-clicked', event, cta.redirect)"
    >
        <template #content-main>
            <div class="event-info">
                <div class="event-label-container">
                    <p v-if="variant === 'artist'" class="artist-card-label">Artist</p>
                    <p v-else class="venue-card-label">Booker</p>
                    <p class="event-label">{{ eventLabel }}</p>
                </div>
                <div class="date-container">
                    <p class="date">{{ eventDate }}</p>
                </div>
                <p v-if="variant === 'artist'" class="venue-name">
                    {{ event.venue_name }}
                </p>
                <p v-if="variant === 'artist'" class="venue-location">
                    {{ venueLocation }}
                </p>
            </div>
            <div v-if="variant === 'venue'" class="booking-info">
                <p v-show="event.event_name" class="event-name">{{ event.event_name }}</p>
                <p class="artists">{{ eventArtists }}</p>
            </div>
            <p class="vibes">{{ eventVibes }}</p>
            <div v-if="variant === 'venue'" class="event-doors-age-cover">
                <p class="doors">{{ `Doors at ${event.start_time.split(' - ')[0]}` }}</p>
                <p class="age">{{ `${event.min_age ? event.min_age + '+' : 'All Ages'}` }}</p>
                <p v-if="event.cover_cost" class="cover">
                    {{ `$${parseFloat(event.cover_cost).toFixed(0)} Cover` }}
                </p>
            </div>
        </template>
        <template #footer-text>
            <p class="label">
                {{ eventPayout.label }}
            </p>
            <p class="value">
                {{ eventPayout.value }}
            </p>
        </template>
    </base-card>
</template>
<script>
    import BaseCard from '@/components/ui/cards/BaseCard.vue'
    import useFormattedData from '@/composables/useFormattedData'
    import { useEventCards } from '@/composables/ui/useEventCards'

    export default {
        name: 'EventCard',
        components: {
            BaseCard
        },
        props: {
            event: {
                type: Object,
                required: true
            },
            variant: {
                type: String,
                required: true,
                validator: (val) => ['artist', 'venue'].includes(val)
            }
        },
        emits: ['cta-clicked', 'card-clicked'],
        setup(props) {
            const {
                eventVibes,
                eventImage,
                eventLabel,
                eventDate,
                eventArtists,
                venueLocation,
                eventPayout,
                cta
            } = useEventCards(props)
            const { formatBigNumbers } = useFormattedData()
            return {
                eventVibes,
                eventImage,
                eventLabel,
                eventDate,
                eventArtists,
                venueLocation,
                eventPayout,
                cta,
                formatBigNumbers
            }
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';

    p {
        margin-bottom: 0px;
    }
    .event-info {
        width: 100%;

        .event-label-container {
            display: flex;
            flex-direction: row;
            align-items: flex-end;
            justify-content: flex-start;
            gap: 8px;

            .venue-card-label {
                background: rgba(186, 135, 255, 0.1);
                padding-left: 4px;
                padding-right: 4px;
                color: #ba87ff;
                text-align: right;
                font-variant-numeric: ordinal;
                @include base-font-12;
                font-weight: 400;
                border-radius: 40px;
            }
            .artist-card-label {
                background: rgba(205, 235, 9, 0.1);
                padding-left: 4px;
                padding-right: 4px;
                color: #cdeb09;
                text-align: right;
                font-variant-numeric: ordinal;
                @include base-font-12;
                font-weight: 400;
                border-radius: 40px;
            }
            .event-label {
                color: #cdeb09;
                text-align: right;
                font-variant-numeric: ordinal;
                @include base-font-12;
                font-weight: 700;
                align-self: flex-end;
                text-wrap: nowrap;
                margin-left: auto;
            }
        }

        .date-container {
            .date {
                color: #dedede;
                font-variant-numeric: ordinal;
                @include base-font-20;
                font-weight: 600;
                line-height: 24px;
                text-wrap: nowrap;
                margin-top: 8px;
            }
        }
        .venue-name,
        .venue-location {
            color: #dedede;
            font-variant-numeric: ordinal;
            @include base-font-12;
        }
    }
    .booking-info {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        max-height: 36px;

        .event-name {
            width: 100%;
            overflow: hidden;
            color: #8b8b8b;
            font-variant-numeric: ordinal;
            text-overflow: ellipsis;
            @include base-font-14;
            font-weight: 600;
            white-space: nowrap;
        }
        .artists {
            overflow: hidden;
            color: #8b8b8b;
            font-variant-numeric: ordinal;
            text-overflow: ellipsis;
            @include base-font-12;
            font-weight: 400;
            margin-top: auto;
        }
    }
    .vibes {
        width: 100%;
        overflow: hidden;
        white-space: nowrap;
        color: #8b8b8b;
        font-variant-numeric: ordinal;
        text-overflow: ellipsis;
        @include base-font-12;
        font-weight: 400;
    }
    .event-doors-age-cover {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 16px;

        .doors,
        .age,
        .cover {
            color: #8b8b8b;
            font-variant-numeric: ordinal;
            @include base-font-14;
        }
    }
    .label {
        color: #8b8b8b;
        font-variant-numeric: ordinal;
        @include base-font-12;
        font-weight: 400;
    }
    .value {
        color: #acacac;
        font-variant-numeric: ordinal;
        @include base-font-16;
        line-height: 24px;
    }
</style>
